import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Container, Row, Col, Form, Alert } from "react-bootstrap";
import axios from "axios";
import { toast } from "react-toastify";
import Preloader from "../../shared/PreLoaders";
import { logActivity } from "../../utils/LogActivity";
import { AuthContext } from "../../Context/AuthContext";
import { Authorization } from "../../Context/Authorization";

const EditMember = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    age: "",
    gender: "",
    vfcCode: "",
    groupCode: "",
    fatherOrHusband: "",
    fatherOrHusbandName: "",
    address: "",
    village: "",
    panchayath: "",
    thaluk: "",
    pinCode: "",
    district: "",
    dob: "",
    phone: "",
    aadhar: "",
    rationCard: "",
    bank: "",
    branch: "",
    accountNumber: "",
    ifscCode: "",
  });
  const [errors, setErrors] = useState({});
  const [vfcOptions, setVfcOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitMessage, setSubmitMessage] = useState("");
  const { userId } = useContext(AuthContext);
  const Api = process.env.REACT_APP_API;

  const validateForm = (data) => {
    const newErrors = {};
    const requiredFields = [
      "name",
      "age",
      "gender",
      "vfcCode",
      "groupCode",
      "fatherOrHusband",
      "fatherOrHusbandName",
      "address",
      "village",
      "panchayath",
      "thaluk",
      "pinCode",
      "district",
      "dob",
      "phone",
      "aadhar",
      "rationCard",
      "bank",
      "branch",
      "accountNumber",
      "ifscCode",
    ];

    requiredFields.forEach((field) => {
      if (!data[field]) newErrors[field] = `${field} is required`;
    });

    return newErrors;
  };

  useEffect(() => {
    setLoading(true);
    const fetchMemberData = async () => {
      try {
        const config = Authorization();

        const response = await axios.get(`${Api}/api/members/${id}`, config);

        setFormData(response.data.member);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching member data", error);
        toast.error("Failed to fetch member data.");
        setLoading(false);
      }
      setLoading(false);
    };

    const fetchVfcOptions = async () => {
      try {
        const config = Authorization();
        const response = await fetch(`${Api}/api/vfcs`, config);
        const data = await response.json();
        setVfcOptions(data.vfcs);
      } catch (error) {
        console.error("Error fetching VFC options", error);
      }
      setLoading(false);
    };

    fetchMemberData();
    fetchVfcOptions();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validateForm(formData);
    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      try {
        const config = Authorization();
        const response = await axios.put(`${Api}/api/members/${id}`, {
          ...formData,
          dob: new Date(formData.dob).toISOString(),
          config,
        });
        setSubmitMessage(response.data.message);
        await logActivity(userId, "Edited Member", "Edit Member Page", Api);
        toast.success("Member updated successfully");
        navigate("/dashboard/member/manage");
      } catch (error) {
        console.error("Error updating member", error);
        toast.error("Failed to update member");
      }
    } else {
      toast.error("Please fix the form errors before submitting");
    }
  };

  return (
    <div>
      <Preloader loading={loading} />

      <div className="page-header">
        <h3 className="page-title">Edit Members</h3>
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="!#" onClick={(event) => event.preventDefault()}>
                VFC
              </a>
            </li>
            <li className="breadcrumb-item active" aria-current="page">
              Edit Members
            </li>
          </ol>
        </nav>
      </div>
      <Container className="add-member-container">
        {submitMessage && <Alert variant="info">{submitMessage}</Alert>}

        <div className="card">
          <div className="card-body">
            <Form className="form-sample" onSubmit={handleSubmit}>
              <Row>
                <Col md={6}>
                  <Form.Group as={Row}>
                    <label column sm={3}>
                      Name
                    </label>
                    <Col sm={9}>
                      <div className="input-container">
                        <Form.Control
                          type="text"
                          id="name"
                          placeholder="Enter Name"
                          value={formData.name}
                          onChange={handleChange}
                          className={errors.name ? "error" : ""}
                        />
                        {errors.name && (
                          <p className="error-message">{errors.name}</p>
                        )}
                      </div>
                    </Col>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group as={Row}>
                    <label column sm={3}>
                      Age
                    </label>
                    <Col sm={9}>
                      <div className="input-container">
                        <Form.Control
                          type="number"
                          id="age"
                          placeholder="Enter Age"
                          value={formData.age}
                          onChange={handleChange}
                          className={errors.age ? "error" : ""}
                        />
                        {errors.age && (
                          <p className="error-message">{errors.age}</p>
                        )}
                      </div>
                    </Col>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group as={Row}>
                    <label column sm={3}>
                      Gender
                    </label>
                    <Col sm={9}>
                      <div className="input-container">
                        <Form.Control
                          as="select"
                          id="gender"
                          name="gender"
                          value={formData.gender}
                          onChange={handleChange}
                          className={errors.gender ? "error" : ""}
                        >
                          <option value="">Select Gender</option>
                          <option value="Male">Male</option>
                          <option value="Female">Female</option>
                        </Form.Control>
                        {errors.gender && (
                          <p className="error-message">{errors.gender}</p>
                        )}
                      </div>
                    </Col>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group as={Row}>
                    <label column sm={3}>
                      VFC Code
                    </label>
                    <Col sm={9}>
                      <div className="input-container">
                        <Form.Control
                          as="select"
                          id="vfcCode"
                          value={formData.vfcCode}
                          onChange={handleChange}
                          className={errors.vfcCode ? "error" : ""}
                        >
                          <option value="">Select VFC Code</option>
                          {vfcOptions.map((option, index) => (
                            <option key={index} value={option.value}>
                              {option.vfcName}
                            </option>
                          ))}
                        </Form.Control>
                        {errors.vfcCode && (
                          <p className="error-message">{errors.vfcCode}</p>
                        )}
                      </div>
                    </Col>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group as={Row}>
                    <label column sm={3}>
                      Father/Husband
                    </label>
                    <Col sm={9}>
                      <div className="input-container">
                        <Form.Control
                          type="text"
                          id="fatherOrHusband"
                          name="fatherOrHusband"
                          placeholder="Enter Father/Husband"
                          value={formData.fatherOrHusband}
                          onChange={handleChange}
                          className={errors.fatherOrHusband ? "error" : ""}
                        />
                        {errors.fatherOrHusband && (
                          <p className="error-message">
                            {errors.fatherOrHusband}
                          </p>
                        )}
                      </div>
                    </Col>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group as={Row}>
                    <label column sm={3}>
                      Father/Husband Name
                    </label>
                    <Col sm={9}>
                      <div className="input-container">
                        <Form.Control
                          type="text"
                          id="fatherOrHusbandName"
                          name="fatherOrHusbandName"
                          placeholder="Enter Father/Husband Name"
                          value={formData.fatherOrHusbandName}
                          onChange={handleChange}
                          className={errors.fatherOrHusbandName ? "error" : ""}
                        />
                        {errors.fatherOrHusbandName && (
                          <p className="error-message">
                            {errors.fatherOrHusbandName}
                          </p>
                        )}
                      </div>
                    </Col>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group as={Row}>
                    <label column sm={3}>
                      Address
                    </label>
                    <Col sm={9}>
                      <div className="input-container">
                        <Form.Control
                          type="text"
                          id="address"
                          name="address"
                          placeholder="Enter Address"
                          value={formData.address}
                          onChange={handleChange}
                          className={errors.address ? "error" : ""}
                        />
                        {errors.address && (
                          <p className="error-message">{errors.address}</p>
                        )}
                      </div>
                    </Col>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group as={Row}>
                    <label column sm={3}>
                      Village
                    </label>
                    <Col sm={9}>
                      <div className="input-container">
                        <Form.Control
                          type="text"
                          id="village"
                          name="village"
                          placeholder="Enter Village"
                          value={formData.village}
                          onChange={handleChange}
                          className={errors.village ? "error" : ""}
                        />
                        {errors.village && (
                          <p className="error-message">{errors.village}</p>
                        )}
                      </div>
                    </Col>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group as={Row}>
                    <label column sm={3}>
                      Panchayath
                    </label>
                    <Col sm={9}>
                      <div className="input-container">
                        <Form.Control
                          type="text"
                          id="panchayath"
                          name="panchayath"
                          placeholder="Enter Panchayath"
                          value={formData.panchayath}
                          onChange={handleChange}
                          className={errors.panchayath ? "error" : ""}
                        />
                        {errors.panchayath && (
                          <p className="error-message">{errors.panchayath}</p>
                        )}
                      </div>
                    </Col>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group as={Row}>
                    <label column sm={3}>
                      Thaluk
                    </label>
                    <Col sm={9}>
                      <div className="input-container">
                        <Form.Control
                          type="text"
                          id="thaluk"
                          name="thaluk"
                          placeholder="Enter Thaluk"
                          value={formData.thaluk}
                          onChange={handleChange}
                          className={errors.thaluk ? "error" : ""}
                        />
                        {errors.thaluk && (
                          <p className="error-message">{errors.thaluk}</p>
                        )}
                      </div>
                    </Col>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group as={Row}>
                    <label column sm={3}>
                      Pin Code
                    </label>
                    <Col sm={9}>
                      <div className="input-container">
                        <Form.Control
                          type="text"
                          id="pinCode"
                          name="pinCode"
                          placeholder="Enter Pin Code"
                          value={formData.pinCode}
                          onChange={handleChange}
                          className={errors.pinCode ? "error" : ""}
                        />
                        {errors.pinCode && (
                          <p className="error-message">{errors.pinCode}</p>
                        )}
                      </div>
                    </Col>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group as={Row}>
                    <label column sm={3}>
                      District
                    </label>
                    <Col sm={9}>
                      <div className="input-container">
                        <Form.Control
                          type="text"
                          id="district"
                          name="district"
                          placeholder="Enter District"
                          value={formData.district}
                          onChange={handleChange}
                          className={errors.district ? "error" : ""}
                        />
                        {errors.district && (
                          <p className="error-message">{errors.district}</p>
                        )}
                      </div>
                    </Col>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group as={Row}>
                    <label column sm={3}>
                      Date of Birth
                    </label>
                    <Col sm={9}>
                      <div className="input-container">
                        <Form.Control
                          type="date"
                          id="dob"
                          name="dob"
                          value={formData.dob}
                          onChange={handleChange}
                          className={errors.dob ? "error" : ""}
                        />
                        {errors.dob && (
                          <p className="error-message">{errors.dob}</p>
                        )}
                      </div>
                    </Col>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group as={Row}>
                    <label column sm={3}>
                      Phone
                    </label>
                    <Col sm={9}>
                      <div className="input-container">
                        <Form.Control
                          type="text"
                          id="phone"
                          name="phone"
                          placeholder="Enter Phone"
                          value={formData.phone}
                          onChange={handleChange}
                          className={errors.phone ? "error" : ""}
                        />
                        {errors.phone && (
                          <p className="error-message">{errors.phone}</p>
                        )}
                      </div>
                    </Col>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group as={Row}>
                    <label column sm={3}>
                      Aadhar Number
                    </label>
                    <Col sm={9}>
                      <div className="input-container">
                        <Form.Control
                          type="text"
                          id="aadhar"
                          name="aadhar"
                          placeholder="Enter Aadhar Number"
                          value={formData.aadhar}
                          onChange={handleChange}
                          className={errors.aadhar ? "error" : ""}
                        />
                        {errors.aadhar && (
                          <p className="error-message">{errors.aadhar}</p>
                        )}
                      </div>
                    </Col>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group as={Row}>
                    <label column sm={3}>
                      Ration Card
                    </label>
                    <Col sm={9}>
                      <div className="input-container">
                        <Form.Control
                          type="text"
                          id="rationCard"
                          name="rationCard"
                          placeholder="Enter Ration Card Number"
                          value={formData.rationCard}
                          onChange={handleChange}
                          className={errors.rationCard ? "error" : ""}
                        />
                        {errors.rationCard && (
                          <p className="error-message">{errors.rationCard}</p>
                        )}
                      </div>
                    </Col>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group as={Row}>
                    <label column sm={3}>
                      Bank
                    </label>
                    <Col sm={9}>
                      <div className="input-container">
                        <Form.Control
                          type="text"
                          id="bank"
                          name="bank"
                          placeholder="Enter Bank Name"
                          value={formData.bank}
                          onChange={handleChange}
                          className={errors.bank ? "error" : ""}
                        />
                        {errors.bank && (
                          <p className="error-message">{errors.bank}</p>
                        )}
                      </div>
                    </Col>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group as={Row}>
                    <label column sm={3}>
                      Branch
                    </label>
                    <Col sm={9}>
                      <div className="input-container">
                        <Form.Control
                          type="text"
                          id="branch"
                          name="branch"
                          placeholder="Enter Branch Name"
                          value={formData.branch}
                          onChange={handleChange}
                          className={errors.branch ? "error" : ""}
                        />
                        {errors.branch && (
                          <p className="error-message">{errors.branch}</p>
                        )}
                      </div>
                    </Col>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group as={Row}>
                    <label column sm={3}>
                      Account Number
                    </label>
                    <Col sm={9}>
                      <div className="input-container">
                        <Form.Control
                          type="text"
                          id="accountNumber"
                          name="accountNumber"
                          placeholder="Enter Account Number"
                          value={formData.accountNumber}
                          onChange={handleChange}
                          className={errors.accountNumber ? "error" : ""}
                        />
                        {errors.accountNumber && (
                          <p className="error-message">
                            {errors.accountNumber}
                          </p>
                        )}
                      </div>
                    </Col>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group as={Row}>
                    <label column sm={3}>
                      IFSC Code
                    </label>
                    <Col sm={9}>
                      <div className="input-container">
                        <Form.Control
                          type="text"
                          id="ifscCode"
                          name="ifscCode"
                          placeholder="Enter IFSC Code"
                          value={formData.ifscCode}
                          onChange={handleChange}
                          className={errors.ifscCode ? "error" : ""}
                        />
                        {errors.ifscCode && (
                          <p className="error-message">{errors.ifscCode}</p>
                        )}
                      </div>
                    </Col>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group as={Row}>
                    <label column sm={3}>
                      Email
                    </label>
                    <Col sm={9}>
                      <div className="input-container">
                        <Form.Control
                          type="text"
                          id="eMail"
                          name="eMail"
                          placeholder="Enter Email"
                          value={formData.eMail}
                          onChange={handleChange}
                          className={errors.eMail ? "error" : ""}
                        />
                        {errors.eMail && (
                          <p className="error-message">{errors.eMail}</p>
                        )}
                      </div>
                    </Col>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group as={Row}>
                    <label column sm={3}>
                      Community
                    </label>
                    <Col sm={9}>
                      <div className="input-container">
                        <Form.Control
                          type="text"
                          id="community"
                          name="community"
                          placeholder="Enter Community"
                          value={formData.community}
                          onChange={handleChange}
                          className={errors.community ? "error" : ""}
                        />
                        {errors.community && (
                          <p className="error-message">{errors.community}</p>
                        )}
                      </div>
                    </Col>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group as={Row}>
                    <label column sm={3}>
                      Work
                    </label>
                    <Col sm={9}>
                      <div className="input-container">
                        <Form.Control
                          type="text"
                          id="work"
                          name="work"
                          placeholder="Enter Work"
                          value={formData.work}
                          onChange={handleChange}
                          className={errors.work ? "error" : ""}
                        />
                        {errors.work && (
                          <p className="error-message">{errors.work}</p>
                        )}
                      </div>
                    </Col>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group as={Row}>
                    <label column sm={3}>
                      Educational Qualification
                    </label>
                    <Col sm={9}>
                      <div className="input-container">
                        <Form.Control
                          type="text"
                          id="educationalQualification"
                          name="educationalQualification"
                          placeholder="Enter Educational Qualification"
                          value={formData.educationalQualification}
                          onChange={handleChange}
                          className={
                            errors.educationalQualification ? "error" : ""
                          }
                        />
                        {errors.educationalQualification && (
                          <p className="error-message">
                            {errors.educationalQualification}
                          </p>
                        )}
                      </div>
                    </Col>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group as={Row}>
                    <label column sm={3}>
                      Income
                    </label>
                    <Col sm={9}>
                      <div className="input-container">
                        <Form.Control
                          type="text"
                          id="income"
                          name="income"
                          placeholder="Enter Income"
                          value={formData.income}
                          onChange={handleChange}
                          className={errors.income ? "error" : ""}
                        />
                        {errors.income && (
                          <p className="error-message">{errors.income}</p>
                        )}
                      </div>
                    </Col>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group as={Row}>
                    <label column sm={3}>
                      WhatsApp No
                    </label>
                    <Col sm={9}>
                      <div className="input-container">
                        <Form.Control
                          type="text"
                          id="whatsAppNo"
                          name="whatsAppNo"
                          placeholder="Enter WhatsApp Number"
                          value={formData.whatsAppNo}
                          onChange={handleChange}
                          className={errors.whatsAppNo ? "error" : ""}
                        />
                        {errors.whatsAppNo && (
                          <p className="error-message">{errors.whatsAppNo}</p>
                        )}
                      </div>
                    </Col>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group as={Row}>
                    <label column sm={3}>
                      Marital Status
                    </label>
                    <Col sm={9}>
                      <div className="input-container">
                        <Form.Control
                          as="select"
                          name="maritialStatus"
                          id="maritialStatus"
                          value={formData.maritialStatus}
                          onChange={handleChange}
                          className={errors.maritialStatus ? "error" : ""}
                        >
                          <option value="">Select Marital Status</option>
                          <option value="Married">Married</option>
                          <option value="Single">Single</option>
                          <option value="Widowed">Widowed</option>
                          <option value="Divorced">Divorced</option>
                        </Form.Control>
                        {errors.maritialStatus && (
                          <p className="error-message">
                            {errors.maritialStatus}
                          </p>
                        )}
                      </div>
                    </Col>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group as={Row}>
                    <label column sm={3}>
                      Family Member Details
                    </label>
                    <Col sm={9}>
                      <div className="input-container">
                        <Form.Control
                          type="text"
                          id="familyMemberDetails"
                          name="familyMemberDetails"
                          placeholder="Enter Family Member Details"
                          value={formData.familyMemberDetails}
                          onChange={handleChange}
                          className={errors.familyMemberDetails ? "error" : ""}
                        />
                        {errors.familyMemberDetails && (
                          <p className="error-message">
                            {errors.familyMemberDetails}
                          </p>
                        )}
                      </div>
                    </Col>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group as={Row}>
                    <label column sm={3}>
                      Profile Photo
                    </label>
                    <Col sm={9}>
                      <div className="input-container">
                        <Form.Control
                          type="file"
                          id="file"
                          onChange={handleChange}
                          className={errors.profilePhoto ? "error" : ""}
                        />
                        {errors.profilePhoto && (
                          <p className="error-message">{errors.profilePhoto}</p>
                        )}
                      </div>
                    </Col>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group as={Row}>
                    <label column sm={3}>
                      Signature
                    </label>
                    <Col sm={9}>
                      <div className="input-container">
                        <Form.Control
                          type="file"
                          id="file"
                          onChange={handleChange}
                          className={errors.signature ? "error" : ""}
                        />
                        {errors.signature && (
                          <p className="error-message">{errors.signature}</p>
                        )}
                      </div>
                    </Col>
                  </Form.Group>
                </Col>
              </Row>

              <button
                type="submit"
                className="btn btn-outline-primary btn-icon-text mt-3"
              >
                <i className="mdi mdi-file-check btn-icon-prepend"></i>
                Submit
              </button>
            </Form>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default EditMember;
